// Shared rendering for the figures on the Sales Performance Dashboard.
//
// The PRD allows one growth calculation for the whole page; the same has to
// hold for how growth is DRAWN, or the tiles and the tables end up disagreeing
// about what a number means. Every surface renders through this module.
//
// Three states, and they must not look alike:
//   absent  — null, because nothing could be compared. Renders as a dash.
//   flat    — a real zero. Renders neutral, never as a fall.
//   moved   — up or down, coloured and signed.
import React from 'react';

import local from '../SalesPerformance.module.css';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// Absent renders as a dash. A real zero renders as zero, and the two must not
// look alike.
export const money = (value) =>
  value === null || value === undefined ? '—' : `₹${inr.format(Math.round(value))}`;

export const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

export const formatChange = (ratio) => {
  if (ratio === null || ratio === undefined) return null;
  return `${ratio > 0 ? '+' : ''}${(ratio * 100).toFixed(1)}%`;
};

// Green above zero, red below, neutral AT zero. Colouring an unchanged period
// red would report steadiness as a decline.
const toneClass = (ratio, { small = true } = {}) => {
  if (!ratio) return local.tileMuted;
  if (small) return ratio > 0 ? local.changeUpSmall : local.changeDownSmall;
  return ratio > 0 ? local.changeUp : local.changeDown;
};

// One growth figure. `large` is the chart's period comparison; everything else
// is a tile or a table cell.
export const Change = ({ value, large = false, absent = '—' }) => {
  const text = formatChange(value);
  if (text === null) return <span className={local.tileMuted}>{absent}</span>;
  return <span className={toneClass(value, { small: !large })}>{text}</span>;
};

// "18 Aug – 25 Aug", so a tile can name the period it compares against without
// spending a line on it.
export const formatRange = (start, end) => {
  if (!start || !end) return null;
  const short = (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  return `${short(start)} – ${short(end)}`;
};

// What the page actually measured, when that is not what was asked for.
//
// The window control is free to ask for a period the synced reports do not
// reach. Saying so is not decoration: without it a seller reads the shortfall
// as a collapse in trading rather than as data that has not arrived.
export const ClippedWindow = ({ data }) => {
  const asked = data?.window;
  const measured = data?.effective_window;
  if (!asked) return null;
  if (measured && measured.start === asked.start && measured.end === asked.end) {
    return null;
  }
  return (
    <div className={local.muted}>
      {measured
        ? `Measured ${formatRange(measured.start, measured.end)} — the synced
           orders do not cover the whole period selected.`
        : 'No synced orders fall in this period.'}
    </div>
  );
};

export default Change;
